/**
 * Packs the frame's uniforms into the layout `NETWORK_SHADER` declares.
 *
 * Deliberately close to `packSurfaceUniforms` - same over-padded vec4 layout, same reason, same
 * camera basis - and deliberately not the same function. The surface's struct carries `field`
 * (`zMin`, `zSpan`, `heightScale`) for a ramp lookup the network pass has no use for, and this one
 * carries the viewport and the surface color, which the surface pass has no use for. Sharing one
 * struct would mean every future field on either view paying for the other's.
 */

import type { Size, ViewCamera } from '../../../types';
import { toCameraBasis } from '../../../geometry/projection';
import { toViewProjectionMatrix } from '../../../geometry/viewProjection';
import {
  toWorldLightDirection,
  type LightingModel,
} from '../../../render/lighting';
import type { AmbientOcclusion } from '../networkTheme';

/** How many lights the shader's struct holds. Matches the surface pass. */
const SHADER_LIGHT_COUNT = 2;

/** Offsets in floats, matching the `Uniforms` struct exactly. Changed together with the shader. */
const NETWORK_UNIFORM_OFFSETS = {
  viewProjection: 0,
  basisRight: 16,
  basisUp: 20,
  basisForward: 24,
  /** Per light: xyz world-space direction, w diffuse. */
  lightDirection: 28,
  /** Per light: rgb color, w specular. */
  lightColor: 36,
  /** rgb fog color, w strength. */
  fog: 44,
  /** ambient, shininess, depthExtent, unused. */
  params: 48,
  /** device-pixel width, device-pixel height, pixel ratio, unused. */
  viewport: 52,
  /** rgb surface color, w unused. */
  surface: 56,
  /** travelling phase in device pixels, pulse strength, lead strength, unused. */
  flow: 60,
  /** strength, halo (0 or 1), sampling radius in device pixels, depth range in world units. */
  occlusion: 64,
} as const;

/** Total size in floats. 16-byte aligned, as a uniform buffer binding must be. */
export const NETWORK_UNIFORM_FLOAT_COUNT = 68;

/** How far around a pixel the occlusion looks for nearer nodes, in CSS pixels. */
export const OCCLUSION_RADIUS_PIXELS = 18;

/**
 * How much nearer a node can be and still shade what is behind it, in world units. Past this the
 * two are far apart in depth and only happen to overlap on screen, and a shadow between them would
 * claim a contact that is not there.
 */
export const OCCLUSION_DEPTH_RANGE = 0.18;

export type NetworkUniformArgs = {
  camera: ViewCamera;
  /** In device pixels, since line widths are resolved against it. */
  viewport: Size;
  pixelRatio: number;
  lighting: LightingModel;
  ambientOcclusion: AmbientOcclusion;
  /** Cleared to this, and what fog and the node rim resolve toward. */
  surface: readonly [number, number, number];
  /** How a reference says which way it points. */
  flow: NetworkFlow;
};

export type NetworkFlow = {
  /**
   * How far the pulses have travelled, in device pixels.
   *
   * Supplied rather than derived from a clock here, because this is called once a frame from a
   * loop that already knows what time it is - and a function that reads the clock itself cannot be
   * tested, cannot be driven at a fixed step for an export, and would make two passes of the same
   * frame disagree.
   */
  phase: number;
  /** 0 leaves the routes static, which is what `prefers-reduced-motion` asks for. */
  pulseStrength: number;
  /** How much dimmer a route is at its source than at its target. */
  leadStrength: number;
};

/**
 * How much dimmer a reference is where it leaves its source than where it arrives.
 *
 * Enough to read as a direction across a short reference and not so much that the source end
 * disappears: at the far end of the estate the fog has already taken most of a line's opacity, and
 * the two multiply. Half is where a reference still reads as one line rather than as a line and a
 * ghost.
 */
export const EDGE_LEAD_STRENGTH = 0.5;

/** How fast a pulse travels, in device pixels a second. */
export const FLOW_PIXELS_PER_SECOND = 115;

/** What a still view carries: the lead gradient, and no motion. */
export const STATIC_NETWORK_FLOW: NetworkFlow = {
  phase: 0,
  pulseStrength: 0,
  leadStrength: EDGE_LEAD_STRENGTH,
};

export const packNetworkUniforms = ({
  camera,
  viewport,
  pixelRatio,
  lighting,
  ambientOcclusion,
  surface,
  flow,
}: NetworkUniformArgs): Float32Array => {
  const data = new Float32Array(NETWORK_UNIFORM_FLOAT_COUNT);
  const basis = toCameraBasis(camera);

  data.set(
    toViewProjectionMatrix(camera, viewport),
    NETWORK_UNIFORM_OFFSETS.viewProjection
  );
  data.set(basis.right, NETWORK_UNIFORM_OFFSETS.basisRight);
  data.set(basis.up, NETWORK_UNIFORM_OFFSETS.basisUp);
  data.set(basis.forward, NETWORK_UNIFORM_OFFSETS.basisForward);

  for (let index = 0; index < SHADER_LIGHT_COUNT; index++) {
    const light = lighting.lights[index];
    const directionAt = NETWORK_UNIFORM_OFFSETS.lightDirection + index * 4;
    const colorAt = NETWORK_UNIFORM_OFFSETS.lightColor + index * 4;

    /* A rig with one light pads the unused slot with zero diffuse, so the shader needs no count
       and cannot read uninitialized memory. */
    if (!light) {
      data[directionAt + 2] = 1;
      continue;
    }

    data.set(toWorldLightDirection(light, basis), directionAt);
    data[directionAt + 3] = light.diffuse;
    data.set(light.color, colorAt);
    data[colorAt + 3] = light.specular;
  }

  data.set(lighting.fog.color, NETWORK_UNIFORM_OFFSETS.fog);
  data[NETWORK_UNIFORM_OFFSETS.fog + 3] = lighting.fog.strength;

  data[NETWORK_UNIFORM_OFFSETS.params] = lighting.ambient;
  data[NETWORK_UNIFORM_OFFSETS.params + 1] = lighting.shininess;
  /* Half-extent of the cube the camera is handed, which bounds the absolute view-axis depth. The
     shader needs it to turn a depth into both a fog amount and a clip z, neither of which the
     view-projection matrix carries. `heightScale` is 1 for every network view - the billboards
     expand along the camera axes and a z-scaled matrix would squash them into ellipses - so this
     is the diagonal of the unit cube. */
  data[NETWORK_UNIFORM_OFFSETS.params + 2] = Math.hypot(
    1,
    1,
    camera.heightScale
  );

  data[NETWORK_UNIFORM_OFFSETS.occlusion] = ambientOcclusion.strength;
  data[NETWORK_UNIFORM_OFFSETS.occlusion + 1] = ambientOcclusion.halo ? 1 : 0;
  data[NETWORK_UNIFORM_OFFSETS.occlusion + 2] =
    OCCLUSION_RADIUS_PIXELS * pixelRatio;
  data[NETWORK_UNIFORM_OFFSETS.occlusion + 3] = OCCLUSION_DEPTH_RANGE;

  data[NETWORK_UNIFORM_OFFSETS.viewport] = viewport.width;
  data[NETWORK_UNIFORM_OFFSETS.viewport + 1] = viewport.height;
  data[NETWORK_UNIFORM_OFFSETS.viewport + 2] = pixelRatio;

  data.set(surface, NETWORK_UNIFORM_OFFSETS.surface);

  data[NETWORK_UNIFORM_OFFSETS.flow] = flow.phase;
  data[NETWORK_UNIFORM_OFFSETS.flow + 1] = flow.pulseStrength;
  data[NETWORK_UNIFORM_OFFSETS.flow + 2] = flow.leadStrength;

  return data;
};
